import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useServiceClassification } from '../../../contexts/ServiceClassificationContext';
import ServicesClassificationNav from './ServicesClassificationNav';

const ServicesHierarchy = () => {
  const { hierarchy, loading, error } = useServiceClassification();
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    if (hierarchy.length) {
      setExpanded(new Set(hierarchy.map((c) => c.id)));
    } else {
      setExpanded(new Set());
    }
  }, [hierarchy]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const stats = useMemo(() => {
    const cats = hierarchy.length;
    const subs = hierarchy.reduce((s, c) => s + (c.subcategories?.length || 0), 0);
    const fams = hierarchy.reduce(
      (s, c) =>
        s + (c.subcategories || []).reduce((ss, sub) => ss + (sub.families?.length || 0), 0),
      0
    );
    return { cats, subs, fams };
  }, [hierarchy]);

  if (loading) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="text-center text-gray-600 py-16">Cargando jerarquía de servicios…</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
        <span className="mx-2">/</span>
        <Link to="/classification" className="hover:text-teal-600">Clasificación</Link>
        <span className="mx-2">/</span>
        <Link to="/classification/services/categories" className="hover:text-teal-600">Servicios</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Vista jerárquica</span>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Vista jerárquica</h1>
        <p className="text-gray-600 text-sm mt-1">
          Clasificación de servicios: categoría → subcategoría → familia.
        </p>
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      </div>

      <ServicesClassificationNav />

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-lg border p-3 text-center">
          <div className="text-xs text-gray-500">Categorías</div>
          <div className="text-xl font-bold text-teal-600">{stats.cats}</div>
        </div>
        <div className="bg-white rounded-lg border p-3 text-center">
          <div className="text-xs text-gray-500">Subcategorías</div>
          <div className="text-xl font-bold text-purple-600">{stats.subs}</div>
        </div>
        <div className="bg-white rounded-lg border p-3 text-center">
          <div className="text-xs text-gray-500">Familias</div>
          <div className="text-xl font-bold text-indigo-600">{stats.fams}</div>
        </div>
      </div>

      {hierarchy.length === 0 && !error ? (
        <div className="bg-white rounded-lg border border-dashed p-8 text-center text-gray-500">
          No hay categorías de servicios. Cree categorías desde el menú de clasificación.
        </div>
      ) : null}

      {hierarchy.length > 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 space-y-2">
          {hierarchy.map((cat) => (
            <div key={cat.id} className="border border-gray-100 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(cat.id)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-teal-50 text-left hover:bg-teal-100/80 transition-colors"
              >
                <span className="text-gray-500 w-5">{expanded.has(cat.id) ? '▼' : '▶'}</span>
                <span className="font-mono text-xs text-teal-800 bg-teal-100 px-1.5 py-0.5 rounded">{cat.code}</span>
                <span className="font-semibold text-gray-900">{cat.name}</span>
              </button>
              {expanded.has(cat.id) && (
                <div className="pl-4 pr-2 py-2 space-y-2 bg-gray-50/80">
                  {(cat.subcategories || []).map((sub) => (
                    <div key={sub.id} className="border-l-2 border-purple-200 pl-3">
                      <div className="flex items-center gap-2 py-1">
                        <span className="font-mono text-xs text-purple-800 bg-purple-100 px-1.5 py-0.5 rounded">
                          {sub.code}
                        </span>
                        <span className="font-medium text-gray-800">{sub.name}</span>
                        <Link
                          to={`/classification/services/familias?subcategory=${sub.id}`}
                          className="text-xs text-teal-600 hover:underline ml-auto"
                        >
                          Gestionar familias
                        </Link>
                      </div>
                      <ul className="mt-1 ml-2 space-y-1 border-l border-indigo-100 pl-3">
                        {(sub.families || []).map((fam) => (
                          <li key={fam.id} className="text-sm text-gray-700 flex flex-wrap items-baseline gap-2">
                            <span className="font-mono text-xs text-indigo-700 bg-indigo-50 px-1 rounded">{fam.code}</span>
                            <span>{fam.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default ServicesHierarchy;
